import { jsxs, jsx } from "react/jsx-runtime";
import { createContext, useState, useCallback, useContext } from "react";
import { X, Info, Award, XCircle, CheckCircle } from "lucide-react";
import { cn } from "../lib/utils.js";
const ToastContext = createContext(void 0);
const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};
const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);
  const showToast = useCallback(
    (message, type = "info", duration = 8e3) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const toast = { id, type, message, duration };
      setToasts((prev) => [...prev, toast]);
      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );
  const showSuccess = useCallback(
    (message, duration) => showToast(message, "success", duration || 8e3),
    [showToast]
  );
  const showError = useCallback(
    (message, duration) => showToast(message, "error", duration || 1e4),
    [showToast]
  );
  const showInfo = useCallback(
    (message, duration) => showToast(message, "info", duration || 8e3),
    [showToast]
  );
  const getToastIcon = (type) => {
    switch (type) {
      case "success":
        return /* @__PURE__ */ jsx(CheckCircle, { className: "w-4 h-4" });
      case "error":
        return /* @__PURE__ */ jsx(XCircle, { className: "w-4 h-4" });
      case "warning":
        return /* @__PURE__ */ jsx(Award, { className: "w-4 h-4" });
      case "info":
      default:
        return /* @__PURE__ */ jsx(Info, { className: "w-4 h-4" });
    }
  };
  const getToastStyles = (type) => {
    switch (type) {
      case "success":
        return {
          container: "bg-emerald-50 dark:bg-emerald-950/90 border-emerald-200 dark:border-emerald-800",
          icon: "text-emerald-600 dark:text-emerald-400",
          text: "text-emerald-900 dark:text-emerald-100",
          closeBtn: "text-emerald-500 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-200"
        };
      case "error":
        return {
          container: "bg-red-50 dark:bg-red-950/90 border-red-200 dark:border-red-800",
          icon: "text-red-600 dark:text-red-400",
          text: "text-red-900 dark:text-red-100",
          closeBtn: "text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
        };
      case "warning":
        return {
          container: "bg-amber-50 dark:bg-amber-950/90 border-amber-200 dark:border-amber-800",
          icon: "text-amber-600 dark:text-amber-400",
          text: "text-amber-900 dark:text-amber-100",
          closeBtn: "text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200"
        };
      case "info":
      default:
        return {
          container: "bg-blue-50 dark:bg-blue-950/90 border-blue-200 dark:border-blue-800",
          icon: "text-blue-600 dark:text-blue-400",
          text: "text-blue-900 dark:text-blue-100",
          closeBtn: "text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-200"
        };
    }
  };
  return /* @__PURE__ */ jsxs(ToastContext.Provider, { value: { showToast, showSuccess, showError, showInfo }, children: [
    children,
    /* @__PURE__ */ jsx("div", { className: "fixed top-4 right-4 z-[100000] flex flex-col gap-2 pointer-events-none", children: toasts.map((toast) => {
      const styles = getToastStyles(toast.type);
      return /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "pointer-events-auto flex items-center gap-3 min-w-[320px] max-w-md",
            "px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm",
            "animate-in slide-in-from-top-2 fade-in duration-300",
            styles.container
          ),
          children: [
            /* @__PURE__ */ jsx("div", { className: cn("flex-shrink-0", styles.icon), children: getToastIcon(toast.type) }),
            /* @__PURE__ */ jsx("p", { className: cn("flex-1 text-sm font-medium leading-relaxed", styles.text), children: toast.message }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => removeToast(toast.id),
                className: cn(
                  "flex-shrink-0 p-1 rounded-md transition-colors duration-200",
                  "hover:bg-white/50 dark:hover:bg-black/20",
                  styles.closeBtn
                ),
                "aria-label": "Close notification",
                children: /* @__PURE__ */ jsx(X, { className: "w-4 h-4" })
              }
            )
          ]
        },
        toast.id
      );
    }) })
  ] });
};
export {
  ToastProvider,
  useToast
};
//# sourceMappingURL=toast-context.js.map
